import React, { useEffect, useState, useRef } from "react";
import * as pdfjsLib from "pdfjs-dist";
import css from "./PdfViewer.module.css";

console.log("pdf.js version", pdfjsLib.version);

pdfjsLib.GlobalWorkerOptions.workerSrc =
  "https://unpkg.com/pdfjs-dist@2.16.105/build/pdf.worker.min.js";

const THUMBNAIL_WIDTH_PX = 88;
const DPR = window.devicePixelRatio;

function PdfViewer({ pdfUrl }) {
  const [numPages, setNumPages] = useState(0);
  const [pdfDoc, setPdfDoc] = useState(null);
  const [aspectRatio, setAspectRatio] = useState(0);

  const thumbnailCanvasRefs = useRef([]);
  const previewCanvasRefs = useRef([]);

  // Capture the time when pdfUrl changes
  const loadStartTimeRef = useRef(null);

  // First load PDF
  useEffect(() => {
    let isCancelled = false;
    let pdf = null;

    // Reset these whenever pdfUrl changes
    setNumPages(0);

    // Start timing when pdfUrl changes
    loadStartTimeRef.current = performance.now();

    async function loadPDF() {
      try {
        pdf = await pdfjsLib.getDocument(pdfUrl).promise;

        if (isCancelled) return;

        const { numPages } = pdf;

        thumbnailCanvasRefs.current = new Array(numPages).fill(null);
        previewCanvasRefs.current = new Array(numPages).fill(null);

        setPdfDoc(pdf);
        setNumPages(numPages);
      } catch (err) {
        console.error(`Error loading PDF (${pdfUrl}):`, err);
      }
    }

    loadPDF();

    // Cleanup
    return () => {
      isCancelled = true;
      pdf?.destroy();
    };
  }, [pdfUrl]);

  // Start rendering once the PDF is loaded
  useEffect(() => {
    if (!pdfDoc || numPages === 0) return;

    // Enure all canvases are ready
    const thumbsReady =
      thumbnailCanvasRefs.current.length === numPages &&
      !thumbnailCanvasRefs.current.some((ref) => !ref);

    const previewsReady =
      previewCanvasRefs.current.length === numPages &&
      !previewCanvasRefs.current.some((ref) => !ref);

    if (thumbsReady && previewsReady) {
      startRenderingPages(pdfDoc, numPages);
    }
  }, [pdfDoc, numPages]);

  async function startRenderingPages(pdf, numPages) {
    const pageNumbers = Array.from({ length: numPages }, (_, i) => i + 1);

    console.log(`RENDERING PDF: ${pdfUrl}`);
    const overallStart = performance.now();

    // Render thumbnails in parallel
    const thumbnailStart = performance.now();
    const thumbnailsPromise = Promise.all(
      pageNumbers.map((pageNum) => renderThumbnail(pdf, pageNum))
    ).then(() => {
      const thumbnailEnd = performance.now();
      console.log(
        `✅ Thumbnails rendered in ${(thumbnailEnd - thumbnailStart).toFixed(
          2
        )}ms`
      );
    });

    // Render full-page previews in parallel
    const previewStart = performance.now();
    const previewsPromise = Promise.all(
      pageNumbers.map((pageNum) => renderFullPage(pdf, pageNum))
    ).then(() => {
      const previewEnd = performance.now();
      console.log(
        `✅ Full-page previews rendered in ${(
          previewEnd - previewStart
        ).toFixed(2)}ms`
      );
    });

    // Wait for both thumbnails and previews to finish rendering
    await Promise.all([thumbnailsPromise, previewsPromise]);

    const overallEnd = performance.now();
    console.log(
      `✅ Rendering (thumbnails + previews) took ${(
        overallEnd - overallStart
      ).toFixed(2)}ms`
    );

    const totalFromPdfSelection = overallEnd - loadStartTimeRef.current;
    console.log(
      `✨ Total time from PDF selection to full render: ${totalFromPdfSelection.toFixed(
        2
      )}ms\n`
    );
    console.log("\n");
  }

  async function renderThumbnail(pdf, pageNum) {
    try {
      const page = await pdf.getPage(pageNum);
      const viewport = page.getViewport({ scale: 1 });
      const scale = THUMBNAIL_WIDTH_PX / viewport.width;
      const scaledViewport = page.getViewport({ scale });

      const canvas = thumbnailCanvasRefs.current[pageNum - 1];
      if (!canvas) {
        console.warn(`No thumbnail canvas for page ${pageNum}`);
        return;
      }

      const canvasContext = canvas.getContext("2d");
      canvas.width = scaledViewport.width * DPR;
      canvas.height = scaledViewport.height * DPR;
      canvasContext.scale(DPR, DPR);

      setAspectRatio(scaledViewport.width / scaledViewport.height);

      const renderTask = page.render({
        canvasContext,
        viewport: scaledViewport,
      });

      await renderTask.promise;
      canvas.style.opacity = "1";
    } catch (err) {
      console.error(`Error rendering thumbnail for page ${pageNum}:`, err);
    }
  }

  async function renderFullPage(pdf, pageNum) {
    try {
      const page = await pdf.getPage(pageNum);
      const viewport = page.getViewport({ scale: 2 });

      const canvas = previewCanvasRefs.current[pageNum - 1];
      if (!canvas) {
        console.warn(`No full-page canvas for page ${pageNum}`);
        return;
      }

      const canvasContext = canvas.getContext("2d");
      canvas.width = viewport.width * DPR;
      canvas.height = viewport.height * DPR;
      canvasContext.scale(DPR, DPR);

      const renderTask = page.render({
        canvasContext,
        viewport,
      });

      await renderTask.promise;
    } catch (err) {
      console.error(`Error rendering full page ${pageNum}:`, err);
    }
  }

  return (
    <div className={css.pdfviewer}>
      {/* Thumbnails */}
      <div className={css.thumbnailsContainer}>
        {numPages !== 0 && (
          <div className={css.thumbnails}>
            {Array.from({ length: numPages }).map((_, index) => (
              <div
                style={{
                  aspectRatio,
                  width: THUMBNAIL_WIDTH_PX,
                }}
                className={css.thumbnail}
                key={`thumb-${index}`}
              >
                <canvas
                  className={`${css.canvas} ${css.thumbnailCanvas}`}
                  ref={(el) => (thumbnailCanvasRefs.current[index] = el)}
                />
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Full-size Preview */}
      <div className={css.preview}>
        {numPages === 0 ? (
          <div>Loading preview...</div>
        ) : (
          Array.from({ length: numPages }).map((_, index) => (
            <canvas
              key={`preview-${index}`}
              className={css.canvas}
              ref={(el) => (previewCanvasRefs.current[index] = el)}
            />
          ))
        )}
      </div>
    </div>
  );
}

export default PdfViewer;
